import React from "react";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MarkdownContent } from "~/components/markdown-content";
import { Card, CardContent } from "~/components/ui/card";
import { ResourceCard } from "~/components/ui/resource-card";
import { CDNImage } from "~/components/ui/cdn-image";
import { Button } from "~/components/ui/button";
import { ExternalLink } from "lucide-react";
import { coachQueryOptions } from "~/lib/query-options";
import {
  createArticleStructuredData,
  createCoachMeta,
} from "~/lib/shared/meta";

export const Route = createFileRoute(
  "/$username/_profile/resources/$resourceId/",
)({
  loader: async ({ context, params }) => {
    const { username, resourceId } = params;

    // Use consistent query options for coach data
    const coach = await context.queryClient.ensureQueryData(
      coachQueryOptions.byUsername(username),
    );

    if (!coach) {
      throw redirect({ to: "/" });
    }

    // Find the resource in the already-loaded coach data
    const resource = coach.resources?.find((r) => r.id === resourceId);
    if (!resource) {
      throw notFound();
    }

    // Get other resources for "related" section
    const relatedResources =
      coach.resources?.filter((r) => r.id !== resourceId).slice(0, 4) || [];

    return { coach, resource, relatedResources };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        title: "Resource | Dream Coach Match",
        meta: [{ name: "description", content: "Coaching resource" }],
      };
    }

    const { coach, resource } = loaderData;
    const description = resource.description || `Resource by ${coach.fullName}`;
    const imageUrl = resource.imageSrc || coach.profilePictureUrl;
    const currentUrl = `https://dreamcoachmatch.com/${params.username}/resources/${params.resourceId}`;

    const metaTags = createCoachMeta(coach.fullName, {
      description,
      ogDescription: description,
      ogImage: imageUrl || undefined,
      ogUrl: currentUrl,
      ogType: "article",
      twitterCard: imageUrl ? "summary_large_image" : "summary",
      twitterImage: imageUrl || undefined,
      canonical: currentUrl,
      author: coach.fullName,
      publishedTime: resource.publishedAt?.toISOString(),
      modifiedTime: resource.publishedAt?.toISOString(),
      section: "Resources",
      keywords: [
        "coaching",
        "resource",
        coach.profile?.coachingTitle || "life coach",
        "guide",
        "tips",
      ],
    });

    // Add structured data for the article
    const structuredData = createArticleStructuredData({
      headline: resource.title,
      description,
      image: imageUrl || undefined,
      author: coach.fullName,
      publishedDate: resource.publishedAt?.toISOString(),
      modifiedDate: resource.publishedAt?.toISOString(),
      url: currentUrl,
    });

    return {
      ...metaTags,
      title: `${resource.title} - ${coach.fullName} | Dream Coach Match`,
      script: [
        {
          type: "application/ld+json",
          children: JSON.stringify(structuredData),
        },
      ],
    };
  },
  component: ResourcePage,
});

function ResourcePage() {
  const { username } = Route.useParams();
  const { coach, resource, relatedResources } = Route.useLoaderData();

  const { data: queryCoach } = useQuery({
    ...coachQueryOptions.byUsername(username),
    initialData: coach,
  });

  if (!queryCoach || !resource) {
    return <div>Loading...</div>;
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 pb-12">
      <Card>
        <CardContent>
          {/* Top Title Section */}
          <div className="mb-8 flex flex-col">
            {resource.publishedAt && (
              <p className="text-brand-700 mb-4 text-sm font-bold">
                Published on{" "}
                {new Intl.DateTimeFormat(undefined).format(
                  resource.publishedAt,
                )}
              </p>
            )}
            <h1 className="text-text-primary font-heading mb-2 text-4xl">
              {resource.title}
            </h1>
            <p className="text-text-secondary mb-4 text-lg">
              {resource.description}
            </p>
            {resource.imageSrc && (
              <CDNImage
                src={resource.imageSrc}
                alt={resource.title}
                width={1024}
                height={512}
                className="aspect-[1/2] w-full rounded-lg object-cover"
              />
            )}
          </div>

          {/* Main Content Section */}
          <div className="prose prose-lg max-w-none leading-relaxed text-gray-700">
            <main>
              <MarkdownContent content={resource.content} />
            </main>

            {resource.externalLinkText && resource.externalLinkUrl && (
              <div className="mb-4 mt-8 flex items-center justify-center">
                <a
                  href={resource.externalLinkUrl}
                  className="text-center no-underline"
                  target="_blank"
                >
                  <Button className="flex items-center gap-2">
                    <span>{resource.externalLinkText}</span>
                    <ExternalLink />
                  </Button>
                </a>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {/* Footer or Related Articles */}
      {0 < relatedResources.length && (
        <div className="mt-16">
          <h2 className="font-heading mb-4 text-2xl">Latest blog posts</h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {relatedResources.map((relatedResource) => (
              <ResourceCard
                key={relatedResource.id}
                resource={relatedResource}
                coachUsername={queryCoach.username}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
